import { Box } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import groupBy from 'lodash/groupBy'
import orderBy from 'lodash/orderBy'
import { format } from 'date-fns-tz'
import {
  BorderlessAccordion,
  CellProps,
  createKeysAndColumnNameArray,
  DenseTable,
  DenseTableRowProps,
  TableType
} from '../../../components'
import { useIntl } from 'react-intl'
import {
  Maybe,
  PaidMembershipFee,
  PaymentExemption
} from '../../../utils/classes/graphql'
import { filterOutNullValues } from '../../../utils/valueAndKeyHelpers'
import { MaybeString } from '../../../utils/types/types'
import { BASIC_DATE_FORMAT } from '../../../utils'
import { MembershipFeesQueryType } from '../../../types'

const useStyles = makeStyles((theme) => ({
  paymentHistoryBox: {
    alignItems: 'stretch',
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    flexShrink: 1,
    [theme.breakpoints.up('md')]: {
      paddingBottom: '2rem',
      paddingLeft: 0,
      paddingRight: '1rem'
    }
  }
}))

export interface PaymentHistoryProps {
  membershipFees: MembershipFeesQueryType
}

interface PaidMembershipFeeRow extends PaidMembershipFee {
  selite?: MaybeString
}

export const PaymentHistory = ({ membershipFees }: PaymentHistoryProps) => {
  const classes = useStyles()
  const { formatMessage } = useIntl()

  const { membershipFeeObligations, paidMembershipFees } = membershipFees

  const paymentExemptions = membershipFeeObligations?.paymentExemptions || []

  // Sort
  const ordered: Maybe<PaidMembershipFee>[] = orderBy(
    paidMembershipFees,
    ['maksujaksoAlkupvm', 'maksujaksoLoppupvm'],
    ['asc', 'asc']
  )

  const membershipFeeText = formatMessage({
    id: 'pages.membershipFeesPage.paymentHistory.membershipFee'
  })
  const membershipFeeIsUnpaidText = formatMessage({
    id: 'pages.membershipFeesPage.paymentHistory.feeIsUnpaid'
  })
  const paidByMemberText = formatMessage({
    id: 'pages.membershipFeesPage.paymentHistory.selfPayment'
  })
  const paymentExemptionsText = formatMessage({
    id: 'pages.membershipFeesPage.paymentHistory.paymentExemption'
  })

  const filtered = filterOutNullValues(ordered)

  // Handling special cases:
  const fixedOrdered = filtered.map((obj: PaidMembershipFee) => {
    const rowObj: DenseTableRowProps<PaidMembershipFeeRow> = { ...obj }
    if (obj.tapahtumatyyppi === 'S') {
      // - event type is clearance (selvitys) but to make it understandable for user we'll use term "membership fee"
      rowObj.selite = membershipFeeText
    } else if (obj.tapahtumatyyppi === 'I') {
      // - event type is membership fee - paid by the member themself
      rowObj.selite = membershipFeeText
      rowObj.maksajanNimi = paidByMemberText
    } else if (obj.viitenumero) {
      // - the fee is unpaid
      rowObj.addAlertClass = true
      rowObj.selite = obj.viitenumero
      rowObj.maksajanNimi = membershipFeeIsUnpaidText
    } else if (obj.summa === null) {
      // - there is a payment exemption on same time
      const maksuvapautus = paymentExemptions.find(
        (mv: Maybe<PaymentExemption>) => {
          if (
            mv?.aloituspvm === obj.maksujaksoAlkupvm &&
            mv?.lopetuspvm === obj.maksujaksoLoppupvm
          ) {
            return true
          }
          return false
        }
      )
      if (maksuvapautus) {
        rowObj.maksajanMaksupvm = paymentExemptionsText
        rowObj.selite = maksuvapautus.vapautussyy
      }
    }
    return rowObj
  })

  // Group by year (verovuosi)
  const grouped = groupBy(
    fixedOrdered,
    (obj) =>
      obj.verovuosi ||
      format(new Date(obj.maksujaksoLoppupvm), 'yyyy', {
        timeZone: 'Europe/Helsinki'
      }) ||
      '-'
  )

  // Sort grouped years (desc)
  const orderedYears = orderBy(Object.keys(grouped), [], ['desc'])

  const keysETC: CellProps<PaidMembershipFeeRow>[] =
    createKeysAndColumnNameArray('pages.membershipFeesPage.paymentHistory.', [
      {
        key: 'maksujaksoAlkupvm',
        translationIdPostfix: 'periodStartDate',
        dateFormat: BASIC_DATE_FORMAT,
        displayWhenNull: '-'
      },
      {
        key: 'maksujaksoLoppupvm',
        translationIdPostfix: 'periodEndDate',
        dateFormat: BASIC_DATE_FORMAT,
        displayWhenNull: '-'
      },
      {
        key: 'verovuosi',
        translationIdPostfix: 'taxYear',
        dateFormat: 'yyyy',
        displayWhenNull: '-'
      },
      {
        key: 'maksajanMaksupvm',
        translationIdPostfix: 'payerPaymentDate',
        dateFormat: BASIC_DATE_FORMAT,
        displayWhenNull: '-',
        hideIfNoValues: true
      },
      {
        key: 'maksajanNimi',
        translationIdPostfix: 'payerName',
        displayWhenNull: '-',
        hideIfNoValues: true
      },
      {
        key: 'selite',
        translationIdPostfix: 'legend',
        hideIfNoValues: true
      },
      {
        key: 'summa',
        translationIdPostfix: 'sum',
        money: true,
        displayWhenNull: '-'
      }
    ])

  return (
    <Box className={classes.paymentHistoryBox}>
      {orderedYears.map((year, index) => {
        return (
          <BorderlessAccordion
            key={'_' + year}
            title={`${year} (${grouped[year].length})`}
            defaultExpanded={!index}
          >
            <DenseTable
              type={TableType.Static}
              cellProps={keysETC}
              data={grouped[year]}
              tableStylingProps={{ addBorderToFirstMobileColumnBox: true }}
            />
          </BorderlessAccordion>
        )
      })}
    </Box>
  )
}
